import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Alert, Button, Col, Input, Modal, Row,
  Select, Spin, notification, Tooltip, Form,
} from 'antd';
import {
  CheckCircleOutlined,
  InfoCircleTwoTone,
  WarningOutlined,
} from '@ant-design/icons';
import { namespaces } from '../../../i18n/i18n.constants';
import './ManageUser.scss';
import { formatTelephone, formatCPF, formatBP } from '../../../utils/format';
import { RoleFromListAll } from '../../../sharedTypes/RoleTypes';
import MatriculationForm from '../../sharedComponents/matriculationForm/MatriculationForm';
import {
  get, post, put, isSuperAgentError,
  ResponseWrapper, RequestWrapper,
} from '../../../dataSource/requests';
import NewPassword from '../../sharedComponents/newPassword/NewPassword';
import { backendResponseCodes } from '../../../utils/responseMessages';

const ManageUser = (props): JSX.Element => {
  interface MatriculationFromFindUser {
    matriculation: string;
    status: string | undefined;
  }

  interface CommonParams {
    email: string;
    name: string;
    password?: string;
    status?: string;
    roleId?: number;
    telephone: string;
  }

  interface RegisterCustomerParams extends CommonParams {
    matriculations?: Array<MatriculationFromFindUser>;
  }

  interface RegisterAdminParams extends CommonParams {
    bp?: string;
    federalRegistration?: string;
  }

  interface EditCustomerParams extends CommonParams {
    bp?: string;
    customerId?: number;
    federalRegistration?: string;
    matriculations?: Array<MatriculationFromFindUser>;
  }

  interface EditAdminParams extends CommonParams {
    bp?: string;
    federalRegistration?: string;
    id?: number;
  }

  interface ManageUserForm {
    type: string;
    role: number;
    creationDate: string;
    name: string;
    email: string;
    telephone: string;
    federalRegistration: string;
    bp: string;
    status: string;
    newPassword: string;
    matriculationsStatus: Array<string>;
  }

  interface Matriculation {
    id: number;
    value: string;
    error: boolean;
    status: string;
    errorMessage: string;
    equalsId: [];
    matriculation?: string;
  }

  interface InternalType {
    val: string;
    name: string;
    type: string;
  }

  interface RolesByType {
    [key: string]: Array<RoleFromListAll>;
  }

  interface InitialValues {
    email: string;
    telephone: string;
    name: string;
    status: string;
    federalRegistration: string;
    bp: string;
    emailConfirmed: boolean;
    type: string;
    matriculations: Array<Matriculation>;
    roleId: number | undefined;
    role: number | undefined;
    creationDate: string;
  }

  interface Status {
    val: string;
    status: string;
  }

  const emailInputReference = useRef<Input | null>(null);
  const objectLocation = useLocation();
  const { id } = useParams();

  const renderTypesArray: Array<InternalType> = [
    { val: '1', name: ' Cooperado', type: 'COOPERATIVE' },
    { val: '2', name: 'Coopercitrus', type: 'COOPERCITRUS' },
  ];

  const INITIAL_STATUS_ARRAY: Array<Status> = [
    { val: 'ACTIVATED', status: ' Ativo' },
    { val: 'DEACTIVATED', status: 'Inativo' },
  ];

  const { t } = useTranslation('namespaces');
  const [formInstance] = Form.useForm();

  const navigate = useNavigate();

  const [state, setState] = useState<boolean>(true);

  // loads while requests are made
  const [loading, setLoading] = useState<boolean>(objectLocation.pathname.startsWith('/user/edit/'));

  // editing flag
  const [editing, setEditing] = useState<boolean>(objectLocation.pathname.startsWith('/user/edit/'));

  const [currentID, setCurrentID] = useState<string | undefined>(id);

  // when equals 1 show the matriculations (1 -> Cooperado)
  const [type, setType] = useState<string>('1');

  const [role, setRole] = useState<number | undefined>(undefined);

  const [rolesByType, setRolesByType] = useState<RolesByType>({});

  // initial field values
  const [initialValues, setInitialValues] = useState<InitialValues>({
    email: '',
    telephone: '',
    name: '',
    status: '1',
    federalRegistration: '',
    bp: '',
    emailConfirmed: false,
    type: '',
    matriculations: [],
    role: undefined,
    roleId: undefined,
    creationDate: '',
  });

  const [initalStatus, setInitialStatus] = useState<Array<string>>([]);

  // status list (starts without verified email)
  const [statusArray, setStatusArray] = useState<Array<Status>>(INITIAL_STATUS_ARRAY);

  // MatriculationForm parameter (starts with 1 register)
  const [matriculations, setMatriculations] = useState<Array<MatriculationFromFindUser>>([
    {
      matriculation: '',
      // ACTIVATED, DEACTIVATED e PENDING_APPROVAL
      status: 'PENDING_APPROVAL',
    },
  ]);

  // controls when to show the modal
  const [visibleModal, setVisibleModal] = useState<boolean>(false);

  // saves the password when editing
  const [newPassword, setNewPassword] = useState<string>('');

  const [matriculationArray, setMatriculationArray] = useState<Array<Matriculation>>([
    {
      id: 1,
      value: '',
      error: false,
      status: '',
      errorMessage: '',
      equalsId: [],
    },
  ]);

  const [
    conditionalRolesArray,
    setConditionalRolesArray,
  ] = useState<Array<RoleFromListAll> | undefined>([]);

  const mask = (rawValue: string): string => {
    const value = rawValue.replace(/\D/g, '');

    if (value.length <= 10) {
      return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6, 10)}`;
    }

    return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
  };

  const statusDisabled = useMemo(() => initialValues.status === 'EXCLUSION_REQUESTED' || initialValues.status === 'EXCLUSION_APPROVED', [initialValues]);

  useEffect(() => {
    const EMAIL_STATUS_ARRAY = [
      { val: 'ACTIVATED', status: ' Ativo' },
      { val: 'DEACTIVATED', status: 'Inativo' },
      { val: 'EXCLUSION_REQUESTED', status: 'Aguardando exclusão' },
      { val: 'EXCLUSION_APPROVED', status: 'Excluído via solicitação do usuário' },
      { val: 'AWAITING_EMAIL_CONFIRMATION', status: 'E-mail não confirmado' },
    ];

    const typesArray: Array<InternalType> = [
      { val: '1', name: ' Cooperado', type: 'COOPERATIVE' },
      { val: '2', name: 'Coopercitrus', type: 'COOPERCITRUS' },
    ];

    const STATUS_ARRAY = [
      { val: 'ACTIVATED', status: ' Ativo' },
      { val: 'DEACTIVATED', status: 'Inativo' },
      { val: 'EXCLUSION_REQUESTED', status: 'Aguardando exclusão' },
      { val: 'EXCLUSION_APPROVED', status: 'Excluído via solicitação do usuário' },
    ];

    const loadAdminData = async (): Promise<void> => {
      let responseGetAdmin: ResponseWrapper | undefined;
      let responseGetAdminError: ResponseWrapper | undefined;
      let genericErrorGetAdmin: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: `/admin/find/${id}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        responseGetAdmin = await requestReference;
      } catch (error: unknown) {
        console.log('responseGetAdminError', responseGetAdminError);
        if (isSuperAgentError(error)) {
          responseGetAdminError = error.response;
        } else {
          genericErrorGetAdmin = error as Error;
        }
      }

      switch (true) {
        case (responseGetAdmin?.body?.code === 1010): {
          // "User found successfully", 1010
          const data = responseGetAdmin?.body.user;

          // // if 3 shows status list with verified email item
          const newStatusArray = data.status === 'AWAITING_EMAIL_CONFIRMATION'
            ? EMAIL_STATUS_ARRAY
            : STATUS_ARRAY;

          setLoading(false);
          setEditing(true);
          setCurrentID(id);
          setType('2');
          setStatusArray(newStatusArray);
          setInitialValues({
            ...data,
            telephone: mask(data.telephone),
          });
          setRole(data.roleId);
          formInstance.setFieldsValue({
            ...data,
            federalRegistration: formatCPF(data.federalRegistration),
            role: data.roleId,
            type: (data.type === 'COOPERCITRUS') ? '2' : '1',
            creationDate: moment(data.creationDate).utc().format('DD/MM/Y HH:mm:ss'),
            telephone: mask(data.telephone),
          });
        } break;

        case (responseGetAdmin?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseGetAdmin?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.General
              .Forbidden.description.pt_br,
          });

          navigate('/user/list');
          break;

        case (responseGetAdmin?.body?.code === -1007):
          // "User not found", -1007
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.User
              .UserNotFound.description.pt_br,
          });

          navigate('/user/list');
          break;

        case (genericErrorGetAdmin?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao buscar os dados do usuário, por favor '
              + 'tente novamente mais tarde',
          });

          navigate('/user/list');
          break;
      }
    };

    const loadCustomerData = async (): Promise<void> => {
      let responseGetUser: ResponseWrapper | undefined;
      let responseGetUserError: ResponseWrapper | undefined;
      let genericErrorGetUser: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: `/customer/find/${id}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        responseGetUser = await requestReference;
      } catch (error: unknown) {
        console.log(error);
        if (isSuperAgentError(error)) {
          responseGetUserError = error.response;
        } else {
          genericErrorGetUser = error as Error;
        }
      }

      switch (true) {
        case (responseGetUser?.body?.code === 1010): {
          // "User found successfully", 1010
          const data = responseGetUser?.body.user;
          // // if 3 shows status list with verified email item
          const newStatusArray = data.status === 'AWAITING_EMAIL_CONFIRMATION' ? EMAIL_STATUS_ARRAY : STATUS_ARRAY;
          const newMatriculationArray: Array<Matriculation> = [];
          const newInitalStatus: Array<string> = [];

          data.matriculations.forEach((item: MatriculationFromFindUser, index: number) => {
            newMatriculationArray.push({
              id: index + 1,
              value: item.matriculation,
              error: false,
              status: 'success',
              errorMessage: '',
              equalsId: [],
            });

            newInitalStatus[index + 1] = item.status;
          });

          setLoading(false);
          setType('1');
          setEditing(true);
          setCurrentID(id);
          setStatusArray(newStatusArray);
          setInitialValues((previous) => {
            return ({
              ...previous,
              ...data,
              telephone: mask(data.telephone),
            });
          });
          setMatriculations(data.matriculations);
          setInitialStatus(newInitalStatus);
          setRole(data.roleId);
          setMatriculationArray(newMatriculationArray);

          formInstance.setFieldsValue({
            ...data,
            role: data.roleId,
            type: (data.type === 'COOPERCITRUS') ? '2' : '1',
            creationDate: moment(data.creationDate).utc().format('DD/MM/Y HH:mm:ss'),
            telephone: mask(data.telephone),
          });
        } break;

        case (responseGetUserError?.body?.code === -1007):
          // "User not found", -1007
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.User
              .UserNotFound.description.pt_br,
          });

          navigate('/user/list');
          break;

        case (responseGetUserError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseGetUserError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error.General
              .Forbidden.description.pt_br,
          });

          navigate('/user/list');
          break;

        case (genericErrorGetUser?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao buscar os dados do usuário, por favor '
              + 'tente novamente mais tarde',
          });

          navigate('/user/list');
          break;
      }
    };

    const loadData = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/role/listAll',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error: unknown) {
        console.log(error);
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 3000): {
          // "Listed roles with sucesses", 3000
          let newRolesByType: RolesByType = { ...rolesByType };

          typesArray.forEach((v: InternalType) => {
            const roles = response
              ?.body
              ?.roles
              ?.filter((item: RoleFromListAll) => item.type === v.type);
            newRolesByType = { ...newRolesByType, [v.val]: roles };
          });

          setRolesByType(newRolesByType);

          const admEdit = objectLocation.pathname.startsWith('/admin/edit/');

          setConditionalRolesArray(newRolesByType[admEdit ? '2' : type]);

          if (objectLocation.pathname.startsWith('/user/edit/')) {
            await loadCustomerData();
          } else if (objectLocation.pathname.startsWith('/admin/edit/')) {
            await loadAdminData();
          }
        } break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        case (responseError != undefined):
        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao buscar os perfis, '
              + 'por favor tente novamente mais tarde.',
          });
          break;
      }
    };

    loadData();
  }, [props]);

  useEffect(() => {
    return () => {
      setState(false);
    };
  }, []);

  const handleMATRICULA = (newMatriculationArray: Array<Matriculation>): void => {
    setMatriculationArray(newMatriculationArray);
  };

  // Use role state to show the "matriculation" fields
  const handleSelectChange = (value: string): void => {
    formInstance.setFieldsValue({
      role: undefined,
    });

    setType(value);
    setConditionalRolesArray(rolesByType[value]);
  };

  const registerCooperado = async (params: RegisterCustomerParams): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = post({
      url: '/customer/createCustomerAsAdmin',
      params,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1001):
        // "User registered successfully", 1001
        notification.success({
          message: 'Sucesso',
          description: backendResponseCodes.Success.User
            .UserRegisteredSuccessfully.description.pt_br,
        });

        navigate('/user/list');
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        navigate('/user/list');
        break;

      case (responseError?.body?.code === -1000):
        // "Email already in use", -1000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .EmailAlreadyInUse.description.pt_br,
        });

        emailInputReference?.current?.focus();
        break;

      case (responseError?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .RoleNotFound.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -3001):
        // "User cannot use this role", -3001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .UserCannotUseThisRole.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -2000):
        // "Matriculation cannot be null", -2000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Matriculation
            .MatriculationCannotBeNull
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -2005):
        // "There are no valid matriculations", -2005
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Matriculation
            .ThereAreNoValidMatriculations.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -19000):
        // "SAPTimeOut", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19001):
        // "SAPNotFound", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19002):
        // "SAPBadRequest", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -2001):
        // "Duplicated matriculations", -2001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Matriculation
            .DuplicatedMatriculations.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao cadastrar o usuário, '
            + 'por favor tente novamente mais tarde.',
        });

        navigate('/user/list');
        break;
    }
  };

  const registerAdmin = async (params: RegisterAdminParams): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = post({
      url: '/admin',
      params,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1001):
        // "User registered successfully", 1001
        notification.success({
          message: 'Sucesso',
          description: backendResponseCodes.Success.User
            .UserRegisteredSuccessfully.description.pt_br,
        });

        navigate('/user/list');
        break;

      case (responseError?.body?.code === -1000):
        // "Email already in use", -1000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .EmailAlreadyInUse.description.pt_br,
        });

        emailInputReference?.current?.focus();
        break;

      case (responseError?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .RoleNotFound.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -3001):
        // "User cannot use this role", -3001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .UserCannotUseThisRole.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });
        navigate('/user/list');
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao registrar o usuario, '
            + 'por favor tente novamente mais tarde.',
        });

        navigate('/user/list');
        break;
    }
  };

  const editCooperado = async (params: EditCustomerParams): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = put({
      url: '/customer/editCustomerAsAdmin',
      params,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1006):
        // "User edited successfully", 1006
        notification.success({
          message: 'Sucesso',
          description: backendResponseCodes.Success.User
            .UserEditedSuccessfully.description.pt_br,
        });

        navigate('/user/list');
        break;

      case (responseError?.body?.code === -2004):
        // "Customer must have at least one matriculation", -2004
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Matriculation
            .CustomerMustHaveAtLeastOneMatriculation
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -1000):
        // "Email already in use", -1000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .EmailAlreadyInUse
            .description.pt_br,
        });

        emailInputReference?.current?.focus();
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -1008):
        // "User cannot use this status", -1008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserCannotUseThisStatus
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .RoleNotFound
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -3001):
        // "User cannot use this role", -3001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .UserCannotUseThisRole
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -2000):
        // "Matriculation cannot be null", -2000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Matriculation
            .MatriculationCannotBeNull
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -2005):
        // "There are no valid matriculations", -2005
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Matriculation
            .ThereAreNoValidMatriculations.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -19000):
        // "SAPTimeOut", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19001):
        // "SAPNotFound", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19002):
        // "SAPBadRequest", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -2001):
        // "Duplicated matriculations", -2001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Matriculation
            .DuplicatedMatriculations
            .description.pt_br,
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden
            .description.pt_br,
        });

        navigate('/user/list');
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao editar o usuário, por favor tente '
            + 'novamente mais tarde.',
        });

        navigate('/user/list');
        break;
    }
  };

  const editAdmin = async (params: EditAdminParams): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = put({
      url: '/admin',
      params,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
      console.log(error);
    }

    switch (true) {
      case (response?.body?.code === 1006):
        // "User edited successfully", 1006
        notification.success({
          message: 'Sucesso',
          description: backendResponseCodes.Success.User
            .UserEditedSuccessfully.description.pt_br,
        });

        navigate('/user/list');
        break;

      case (responseError?.body?.code === -1007):
        // "User not found", -1007
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserNotFound.description.pt_br,
        });
        navigate('/user/list');
        break;

      case (responseError?.body?.code === -1008):
        // "User cannot use this status", -1008
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .UserCannotUseThisStatus.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -1000):
        // "Email already in use", -1000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.User
            .EmailAlreadyInUse.description.pt_br,
        });

        emailInputReference?.current?.focus();
        break;

      case (responseError?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .RoleNotFound.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -3002):
        // "User cannot edit own role", -3002
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .UserCannotEditOwnRole.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -3003):
        // "At least one admin should be on system", -3003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .AtLeastOneAdminShouldBeOnSystem.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -3004):
        // "Role type cannot be changed", -3004
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .RoleTypeCannotBeChanged.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -3001):
        // "User cannot use this role", -3001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Role
            .UserCannotUseThisRole.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General
            .Forbidden.description.pt_br,
        });

        navigate('/user/list');
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao editar o usuario, '
            + 'por favor tente novamente mais tarde.',
        });

        navigate('/user/list');
        break;
    }
  };

  const checkMatriculationsErrors = (): boolean => {
    const stateCopy = matriculationArray;

    for (let index = 0; index < matriculationArray.length; index += 1) {
      if (matriculationArray?.[index]?.error) {
        stateCopy[index].error = true;

        setMatriculationArray(stateCopy);

        return false;
      }

      if (matriculationArray[index].value.length < 10) {
        stateCopy[index].error = true;
        stateCopy[index].errorMessage = 'Matrícula menor que 10 dígitos';

        setMatriculationArray(stateCopy);

        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (values: ManageUserForm): Promise<void> => {
    setLoading(true);

    // set common parameters
    const params: CommonParams = {
      email: values.email.toLowerCase(),
      name: values.name,
      telephone: values.telephone.replace(/\D/g, ''), // Only numbers
    };

    if (!editing) {
      // Register
      params.password = values.newPassword;

      if (values.type === '1') {
        const registerCooperadoParams: RegisterCustomerParams = params;
        // Register Cooperado user
        if (checkMatriculationsErrors()) {
          registerCooperadoParams.roleId = values.role;
          registerCooperadoParams.status = 'AWAITING_EMAIL_CONFIRMATION';

          const newMatriculations = matriculationArray.map((v) => ({
            matriculation: v.value,
            status: values.matriculationsStatus[v.id],
          }));

          // remove empty lines
          registerCooperadoParams.matriculations = newMatriculations.filter((v) => v.matriculation);

          // call request
          await registerCooperado(registerCooperadoParams);
        }
      } else {
        const registerCoopercitrusParams: RegisterAdminParams = params;
        // Register Coopercitrus user
        registerCoopercitrusParams.status = 'ACTIVATED';
        registerCoopercitrusParams.roleId = values.role;
        registerCoopercitrusParams.federalRegistration = values.federalRegistration.replace(/\D/g, '');
        registerCoopercitrusParams.bp = values.bp.replace(/\D/g, '');

        await registerAdmin(registerCoopercitrusParams);
      }
    } else {
      // Update user

      if (!currentID) {
        notification.error({
          message: 'Erro',
          description: 'Houve um erro ao buscar os dados do usuario',
        });

        return;
      }

      params.roleId = values.role;
      params.status = values.status;

      if (newPassword) {
        params.password = newPassword;
      }

      if (type === '1') {
        const editCooperadoParams: EditCustomerParams = params;
        editCooperadoParams.customerId = Number.parseInt(currentID, 10);

        // Update Cooperado user
        if (checkMatriculationsErrors()) {
          const newMatriculations = matriculationArray.map((v) => ({
            matriculation: v.value,
            status: values.matriculationsStatus[v.id],
          }));

          // remove empty lines
          editCooperadoParams.matriculations = newMatriculations.filter((v) => v.matriculation);

          // call request
          await editCooperado(editCooperadoParams);
        }
      } else {
        const editAdminParams: EditAdminParams = params;
        editAdminParams.id = Number.parseInt(currentID, 10);
        editAdminParams.federalRegistration = values.federalRegistration.replace(/\D/g, '');
        editAdminParams.bp = values.bp.replace(/\D/g, '');

        // Update Coopercitrus user
        await editAdmin(editAdminParams);
      }
    }

    setLoading(false);
  };

  const showModal = (): void => {
    setVisibleModal(true);
  };

  const checkEmailValidation = (): JSX.Element => {
    if (initialValues.emailConfirmed === false) {
      return (
        <Tooltip title="Email não confirmado">
          <InfoCircleTwoTone twoToneColor="#eb2f96" />
        </Tooltip>
      );
    } return (
      <Tooltip title="Email confirmado">
        <CheckCircleOutlined style={{ color: 'green' }} />
      </Tooltip>
    );
  };

  const handleOkAtPasswordModal = async (): Promise<void> => {
    let values;

    try {
      values = await formInstance.validateFields(['newPassword', 'confirmPassword']);
    } catch (error: unknown) {
      console.log(error);
      return;
    }

    // set the password and hide modal
    setNewPassword(values.newPassword);
    setVisibleModal(false);
  };

  const handleCancelAtPasswordModal = (): void => {
    setVisibleModal(false);
  };

  const handleOkAtCancelModal = (): void => {
    navigate('/user/list');
  };

  const handleCancel = (): void => {
    let updated = false;

    if (editing === true) {
      if (initialValues.name !== '' && formInstance.getFieldsValue().name !== '') {
        if (newPassword !== '') {
          updated = true;
        }

        if (!objectLocation.pathname.startsWith('/admin/edit/')) {
          for (let index = 0; index < initialValues.matriculations.length; index += 1) {
            if (initialValues?.matriculations?.[index]?.status !== formInstance.getFieldsValue()
              .matriculationsStatus[index + 1]) {
              updated = true;
            }
          }

          if (matriculationArray.length === initialValues.matriculations.length) {
            for (let index = 0; index < initialValues.matriculations.length; index += 1) {
              if (initialValues?.matriculations?.[index]
                ?.matriculation !== matriculationArray?.[index]?.value) {
                updated = true;
              }
            }
          }

          if (initialValues.matriculations.length !== matriculationArray.length) {
            updated = true;
          }
        }

        if (initialValues.name !== formInstance.getFieldsValue().name) {
          updated = true;
        }

        if (initialValues.status !== formInstance.getFieldsValue().status) {
          updated = true;
        }

        if (initialValues.email.toLowerCase() !== formInstance
          .getFieldsValue().email.toLowerCase()) {
          updated = true;
        }

        if (initialValues.telephone !== formInstance.getFieldsValue().telephone) {
          updated = true;
        }

        if (initialValues.roleId !== formInstance.getFieldsValue().role) {
          updated = true;
        }
      }

      if (updated === true) {
        Modal.confirm({
          title: 'Os dados alterados serão perdidos!',
          content: 'Tem certeza que deseja cancelar?',
          okText: 'Sim, cancelar sem salvar',
          cancelText: 'Não',
          autoFocusButton: null,
          icon: (<WarningOutlined />),
          onOk: handleOkAtCancelModal,
          onCancel: () => {
            //
          },
        });
      } else {
        navigate('/user/list');
      }
    } else {
      navigate('/user/list');
    }
  };

  const validatePhone = (_rule: any, value: string): Promise<void | Error> => {
    if (value === '') {
      return Promise.reject(new Error('Por favor, insira um telefone.'));
    }

    const numbers = value.replace(/\D/g, ''); // Only numbers

    if (numbers.length !== 10 && numbers.length !== 11) {
      return Promise.reject(new Error('Por favor, insira um telefone válido.'));
    }

    return Promise.resolve();
  };

  const validateCpf = (_rule: any, value: string): Promise<void | Error> => {
    if (value === '') {
      return Promise.reject(new Error('Por favor, insira um cpf.'));
    }

    const numbers = value.replace(/\D/g, ''); // Only numbers

    if (numbers.length !== 11) {
      return Promise.reject(new Error('Por favor, insira um cpf válido.'));
    }

    return Promise.resolve();
  };

  return (
    <div>
      <div className="service-title">
        {editing ? 'Editar ' : 'Cadastrar '}
        usuário
      </div>
      <div className="ManageUser">
        <Spin
          tip="Carregando..."
          spinning={loading}
        >
          <Form
            scrollToFirstError
            className="form-inner-container"
            layout="vertical"
            onFinish={handleSubmit}
            form={formInstance}
            initialValues={{
              type: initialValues.type || type || undefined,
              role: initialValues.role || role || undefined,
              creationDate: moment(initialValues.creationDate).format('DD/MM/Y'),
              name: initialValues.name,
              email: initialValues.email,
              telephone: initialValues.telephone,
              status: editing ? initialValues.status : undefined,
            }}
          >
            <Row>
              <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                <h2>Dados pessoais</h2>
              </Col>
            </Row>
            <Row>
              <Col xs={{ span: 24 }} lg={{ span: editing ? 8 : 12 }}>
                <Form.Item
                  label="Tipo"
                  name="type"
                  rules={[
                    {
                      message: 'O campo não pode ser vazio',
                    },
                    {
                      required: true, message: 'Por favor, selecione um tipo.',
                    },
                  ]}
                >
                  <Select
                    showSearch
                    onChange={handleSelectChange}
                    placeholder="Tipo"
                    // don't let it change when editing
                    disabled={editing}
                  >
                    {
                      renderTypesArray.map((v) => (
                        <Select.Option key={v.val} value={v.val}>
                          {v.name}
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} lg={{ span: editing ? 8 : 12 }}>
                <Form.Item
                  label="Perfil"
                  name="role"
                  rules={[
                    {
                      required: true,
                      message: 'Por favor, selecione um perfil.',
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Perfil"
                    disabled={statusDisabled}
                  >
                    {
                      conditionalRolesArray?.map((v) => (
                        <Select.Option key={v.id} value={v.id}>
                          {v.name}
                        </Select.Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>
              {
                editing
                  ? (
                    <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                      <Form.Item
                        label="Data do cadastro"
                        name="creationDate"
                      >
                        <Input
                          type="text"
                          className="ant-input"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  )
                  : null
              }
            </Row>
            <Row>
              <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                <Form.Item
                  label="Nome"
                  name="name"
                  rules={
                    editing
                      ? []
                      : [
                        {
                          message: 'O campo não pode ser vazio',
                        },
                        {
                          required: true, message: 'Por favor, insira um nome.',
                        },
                        {
                          max: 150, message: 'O nome deve conter no máximo 150 caracteres!',
                        },
                        {
                          min: 3, message: 'O nome deve conter no mínimo 3 caracteres!',
                        },
                      ]
                  }
                  getValueFromEvent={
                    (eventt): string => {
                      return eventt.target.value.replace(/[^A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ' ]+$/, '');
                    }
                  }
                >
                  <Input
                    name="titulo"
                    placeholder="Nome"
                    disabled={editing}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                <Form.Item
                  label="E-mail"
                  name="email"
                  rules={
                    editing
                      ? []
                      : [
                        {
                          required: true,
                          message: 'Por favor, insira um email.',
                        },
                        {
                          type: 'email',
                          message: 'Formato de email inválido.',
                        },
                        {
                          pattern: /^[A-z,0-9,@,.,_,-]+$/,
                          message: 'Não utilize caracteres especiais, ou acentuados.',
                        },
                      ]
                  }
                >
                  <Input
                    autoComplete="new-email"
                    type="email"
                    ref={emailInputReference}
                    placeholder="E-mail"
                    suffix={
                      editing
                        && formInstance.getFieldsValue().type === '1'
                        ? checkEmailValidation()
                        : null
                    }
                    disabled={editing}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                <Form.Item
                  label="Telefone"
                  name="telephone"
                  rules={
                    editing
                      ? []
                      : [
                        {
                          validateTrigger: ['onBlur'],
                          validator: validatePhone,
                        },
                        {
                          required: true,
                          message: 'Telefone é um campo obrigatório.',
                        },
                      ]
                  }
                  getValueFromEvent={
                    (eventt): string => {
                      return formatTelephone(eventt.target.value);
                    }
                  }
                >
                  <Input
                    type="text"
                    placeholder="(00) 00000-0000"
                    className="ant-input"
                    disabled={editing}
                  />
                </Form.Item>
              </Col>
              {
                initialValues?.type === 'COOPERCITRUS' && (
                  <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                    <Form.Item
                      label="CPF"
                      name="federalRegistration"
                      rules={
                        editing
                          ? []
                          : [
                            {
                              validateTrigger: ['onBlur'],
                              validator: validateCpf,
                            },
                            {
                              required: true,
                              message: 'Cpf é um campo obrigatório.',
                            },
                          ]
                      }
                      getValueFromEvent={
                        (eventt): string => {
                          return formatCPF(eventt.target.value);
                        }
                      }
                      normalize={
                        (inputValue): string => {
                          return inputValue.replace(/\D/g, '');
                        }
                      }
                    >
                      <Input
                        type="text"
                        placeholder="000.000.000-00"
                        className="ant-input"
                        disabled={editing}
                      />
                    </Form.Item>
                  </Col>
                )
              }
              {
                initialValues?.type === 'COOPERCITRUS' && (
                  <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                    <Form.Item
                      label="BP"
                      name="bp"
                      rules={
                        editing
                          ? []
                          : [
                            {
                              required: true,
                              message: 'BP é um campo obrigatório.',
                            },
                          ]
                      }
                    >
                      <Input
                        type="number"
                        onBlur={
                          (eventt): void => {
                            formInstance.setFieldsValue({
                              bp: eventt.target.value.replace(/.*/, formatBP(eventt.target.value)),
                            });
                          }
                        }
                        maxLength={10}
                        className="ant-input"
                        disabled={editing}
                      />
                    </Form.Item>
                  </Col>
                )
              }
            </Row>
            <Row>
              {
                editing
                && (
                  <Col xs={{ span: 24 }} lg={editing ? { span: 12 } : { span: 8 }}>
                    <Form.Item
                      label="Status"
                      name="status"
                      rules={
                        editing
                          ? []
                          : [
                            {
                              message: 'O campo não pode ser vazio',
                            },
                            {
                              required: true, message: 'Por favor, selecione um status.',
                            },
                          ]
                      }
                    >
                      <Select
                        showSearch
                        placeholder="Status"
                        disabled={editing}
                      >
                        {
                          statusArray.map((v) => (
                            <Select.Option key={v.val} value={v.val}>
                              {v.status}
                            </Select.Option>
                          ))
                        }
                      </Select>
                    </Form.Item>
                  </Col>
                )
              }

              {
                !editing
                && !statusDisabled && (
                  <Col span={24}>
                    <NewPassword form={formInstance} vertical={3} />
                  </Col>
                )
              }

              {
                editing
                && (
                  <div>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }} style={{ textAlign: 'right' }}>
                      <div className="ant-row ant-form-item">
                        <div className="ant-col ant-form-item-label">
                          <div>&nbsp;</div>
                        </div>
                        <div className="ant-col ant-form-item-control-wrapper">
                          <Button type="primary" onClick={showModal} disabled={statusDisabled}>
                            Alterar senha
                          </Button>
                        </div>
                      </div>
                    </Col>

                    <Modal
                      className="password-modal"
                      title={<h3 style={{ margin: 0 }}>Alterar senha</h3>}
                      visible={visibleModal}
                      onOk={handleOkAtPasswordModal}
                      onCancel={handleCancelAtPasswordModal}
                    >
                      {
                        // destroys element if not active
                        (newPassword || visibleModal)
                        && <NewPassword form={formInstance} vertical={2} />
                      }
                    </Modal>
                  </div>
                )
              }
            </Row>

            {
              newPassword
              && (
                <Alert
                  message="É necessário salvar as alterações para que a senha seja atualizada"
                  type="info"
                  showIcon
                />
              )
            }

            {
              type === '1'
              && (
                <Row>
                  <Col
                    xs={{ span: 24 }}
                    style={{ marginTop: 20, paddingTop: 20, borderTop: '2px solid #cccccc' }}
                  >
                    <h2>Matrícula</h2>
                    <MatriculationForm
                      handleMATRICULA={handleMATRICULA}
                      matriculationArray={matriculationArray}
                      initalStatus={initalStatus}
                    />
                  </Col>
                </Row>
              )
            }

            <Row style={{ marginTop: 20 }}>
              <Col span={12}>
                <Button
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
              </Col>

              <Col span={12}>
                <Form.Item
                  style={{ float: 'right' }}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={statusDisabled}
                  >
                    {editing ? 'Salvar alterações' : 'Salvar'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

ManageUser.propTypes = {};

ManageUser.defaultProps = {};

export default ManageUser;
